import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar as CalendarIcon, Download, Trash2, Eye, AlertTriangle } from "lucide-react";
import { useState, useMemo } from "react";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { Ciclo as BaseCiclo } from "@shared/schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import * as XLSX from "xlsx";
import { useSetores } from "@/hooks/use-setores";
import { useVariedadesDoCacho } from "@/hooks/use-variedades-cacho";
import { useToleranciaDias } from "@/hooks/use-tolerancia";
import { algumMangaraVencido, cicloEmJanelaDeColheita } from "@shared/ciclo-fechamento";
import { hojeNaFazenda } from "@/lib/data-fazenda";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import { PageToolbar } from "@/components/layout/page-toolbar";
// Extended Ciclo type with harvest prediction dates
type Ciclo = BaseCiclo & {
  dataPrevisaoInicio: Date | null;
  dataPrevisaoFim: Date | null;
  cor?: any;
  setor?: any;
  variedade?: any;
  remanescente?: number;
  percentualColhido?: number;
  percentualPerda?: number;
  _count?: {
    mangaras: number;
    colheitas: number;
    perdas: number;
  };
};

type FiltroPeriodo = "em_andamento" | "finalizar_ciclo" | "proximo" | "finalizados" | "tudo" | "personalizado";

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_CICLOS = {
  periodo: "em_andamento",
  setor: "todos",
  cor: "todas",
  variedade: "todas",
  de: "",
  ate: "",
  q: "",
};
const PERMITIDOS_CICLOS = {
  periodo: ["em_andamento", "finalizar_ciclo", "proximo", "finalizados", "tudo", "personalizado"],
} as const;

export default function ControleFitasPage() {
  // Os seis filtros na URL. Antes a tela ESCREVIA um `?back=…` com todos eles ao
  // abrir o ciclo e só LIA `?filtro=` na volta — a URL carregava o estado e a tela
  // o ignorava. Agora a URL é a fonte, e `?back=` (mais o `?filtro=`, que nenhuma
  // tela do repo gerava) saem de cena: quem volta é o `history.back()`.
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_CICLOS, PERMITIDOS_CICLOS);
  const { abrir } = useNavegacao();
  const filtroPeriodo = filtros.periodo as FiltroPeriodo;
  const setorId = filtros.setor;
  const corFitaId = filtros.cor;
  const variedadeId = filtros.variedade;
  const dataInicio = filtros.de;
  const dataFim = filtros.ate;
  const busca = filtros.q;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cicloToDelete, setCicloToDelete] = useState<Ciclo | null>(null);
  const { toast } = useToast();

  const { data: todosOsCiclos, isLoading } = useQuery<Ciclo[]>({
    queryKey: ["/api/ciclos"],
  });

  const { data: cores } = useQuery<any[]>({
    queryKey: ["/api/cores"],
  });

  const { data: mangaras } = useQuery<any[]>({
    queryKey: ["/api/mangaras"],
  });

  const { data: colheitas } = useQuery<any[]>({
    queryKey: ["/api/colheitas"],
  });

  const { data: perdas } = useQuery<any[]>({
    queryKey: ["/api/perdas"],
  });

  // Controle de Fitas é tela de banana: o filtro "Variedade" listava Pêra Rio
  // (citros), que nunca tem ciclo. Fail-open — ver `useVariedadesDoCacho`.
  const { variedades } = useVariedadesDoCacho();

  // Lista completa para lookups históricos; produtivos para filtros de seleção
  const { setores } = useSetores();
  const { setores: setoresProdutivos } = useSetores({ somenteProdutivos: true });

  // Número, lido e convertido num lugar só — `parametros.valor` é text no banco.
  const toleranciaDias = useToleranciaDias();

  // Helper function to check if ciclo is in harvest phase
  const isCicloEmColheita = (ciclo: Ciclo): boolean => {
    if (ciclo.status !== "ATIVO") return false;

    const cicloMangaras = mangaras?.filter(m => m.cicloId === ciclo.id) || [];
    if (cicloMangaras.length === 0) return false;

    // Data civil, não semana ISO — ver `cicloEmJanelaDeColheita`. A versão
    // anterior comparava semanas e desistia quando o ano previsto diferia do
    // corrente, escondendo o ano inteiro todo ciclo que colhe no ano seguinte.
    return cicloEmJanelaDeColheita(
      cicloMangaras.map(m => m.dataPrevistaColheita),
      toleranciaDias,
      hojeNaFazenda(),
    );
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/ciclos/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      toast({
        title: "Ciclo excluído",
        description: "O ciclo foi excluído com sucesso",
      });
      setDeleteDialogOpen(false);
      setCicloToDelete(null);
    },
    onError: (error: Error) => {
      let errorMessage = "Erro ao excluir o ciclo";
      try {
        const match = error.message.match(/\d+: (.+)/);
        if (match) {
          const jsonPart = match[1];
          const parsed = JSON.parse(jsonPart);
          errorMessage = parsed.error || errorMessage;
        }
      } catch {
        errorMessage = error.message;
      }
      
      toast({
        title: "Não foi possível excluir",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleDeleteClick = (ciclo: Ciclo, e: React.MouseEvent) => {
    e.stopPropagation();
    setCicloToDelete(ciclo);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (cicloToDelete) {
      deleteMutation.mutate(cicloToDelete.id);
    }
  };

  // Função para verificar se um ciclo está atrasado
  const isCicloAtrasado = (ciclo: Ciclo): boolean => {
    const cicloMangaras = mangaras?.filter(m => m.cicloId === ciclo.id) || [];

    // Era `+ 15` cravado, ignorando a tolerância configurada (35 na Boa Vista):
    // o ciclo era pintado de atrasado 20 dias cedo demais. E o `new Date(str)`
    // + `setHours` lia a data em UTC e reescrevia em hora local, o que a oeste
    // de UTC volta um dia (a mesma família do PR #163).
    return algumMangaraVencido(
      cicloMangaras.map(m => m.dataPrevistaColheita),
      toleranciaDias,
      hojeNaFazenda(),
    );
  };

  // Helper function to check if ciclo has any mangaras from selected variety
  const cicloHasVariedade = (cicloId: string, selectedVariedadeId: string) => {
    const cicloMangaras = mangaras?.filter(m => m.cicloId === cicloId) || [];
    if (!selectedVariedadeId || selectedVariedadeId === "todas") {
      return cicloMangaras.length > 0;
    }
    return cicloMangaras.some(m => {
      const setor = setores?.find(s => s.id === m.setorId);
      return setor?.variedadeId === selectedVariedadeId;
    });
  };

  // Função para calcular o período baseado no filtro
  const calcularPeriodo = () => {
    switch (filtroPeriodo) {
      case "em_andamento":
        return "EM_ANDAMENTO";
      case "finalizar_ciclo":
        return "FINALIZAR_CICLO";
      case "proximo":
        return "PROXIMO";
      case "finalizados":
        return "FINALIZADOS";
      case "tudo":
        return "TUDO";
      case "personalizado":
        if (dataInicio && dataFim) {
          return {
            inicio: new Date(dataInicio),
            fim: new Date(dataFim)
          };
        }
        return null;
      default:
        return null;
    }
  };

  // Process and filter ciclos data with calculated metrics respecting setor/variedade filters
  const filteredData = useMemo(() => {
    if (!todosOsCiclos || !mangaras) return [];

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const periodo = calcularPeriodo();

    // Helper to get filtered mangaras for a ciclo based on current setor/variedade filters
    const getFilteredMangaras = (cicloId: string) => {
      let cicloMangaras = mangaras.filter(m => m.cicloId === cicloId);
      
      // Filter by setor if selected
      if (setorId !== "todos") {
        cicloMangaras = cicloMangaras.filter(m => m.setorId === setorId);
      }
      
      // Filter by variety if selected
      if (variedadeId !== "todas") {
        cicloMangaras = cicloMangaras.filter(m => {
          const setor = setores?.find(s => s.id === m.setorId);
          return setor?.variedadeId === variedadeId;
        });
      }
      
      return cicloMangaras;
    };

    // Helper to get filtered colheitas for a ciclo
    const getFilteredColheitas = (cicloId: string) => {
      let cicloColheitas = colheitas?.filter(c => c.cicloId === cicloId) || [];
      
      if (setorId !== "todos") {
        cicloColheitas = cicloColheitas.filter(c => c.setorId === setorId);
      }
      
      if (variedadeId !== "todas") {
        cicloColheitas = cicloColheitas.filter(c => {
          const setor = setores?.find(s => s.id === c.setorId);
          return setor?.variedadeId === variedadeId;
        });
      }
      
      return cicloColheitas;
    };

    // Helper to get filtered perdas for a ciclo
    const getFilteredPerdas = (cicloId: string) => {
      let cicloPerdas = perdas?.filter(p => p.cicloId === cicloId) || [];
      
      if (setorId !== "todos") {
        cicloPerdas = cicloPerdas.filter(p => p.setorId === setorId);
      }
      
      if (variedadeId !== "todas") {
        cicloPerdas = cicloPerdas.filter(p => {
          const setor = setores?.find(s => s.id === p.setorId);
          return setor?.variedadeId === variedadeId;
        });
      }
      
      return cicloPerdas;
    };

    // First pass: filter ciclos that have data after applying setor/variedade filters
    let result = todosOsCiclos.filter(ciclo => {
      // Check if ciclo has mangaras after filtering
      const filteredMangaras = getFilteredMangaras(ciclo.id);
      // Allow ATIVO ciclos with no mangaras (orphans) to appear so they can be deleted
      // Only when no setor/variedade filter is active (filters hide unrelated data, not orphans)
      const isOrfaoAtivo = filteredMangaras.length === 0 && ciclo.status === 'ATIVO' && setorId === 'todos' && variedadeId === 'todas';
      if (filteredMangaras.length === 0 && !isOrfaoAtivo) return false;

      // Filter by ribbon color
      if (corFitaId !== "todas" && ciclo.corFitaId !== corFitaId) {
        return false;
      }

      // Orphaned ATIVO ciclos appear under em_andamento and tudo, not in finalizados/proximo/finalizar_ciclo
      if (isOrfaoAtivo) {
        if (periodo === "TUDO" || periodo === "EM_ANDAMENTO") return true;
        return false;
      }

      // Period filter - uses calculated status from dates
      if (periodo === "TUDO") {
        return true;
      }
      
      // Calculate status for this ciclo to filter by it
      const dataPrevisaoInicio = ciclo.dataPrevisaoInicio ? new Date(ciclo.dataPrevisaoInicio) : null;
      const dataPrevisaoFim = ciclo.dataPrevisaoFim ? new Date(ciclo.dataPrevisaoFim) : null;
      if (dataPrevisaoInicio) dataPrevisaoInicio.setHours(0, 0, 0, 0);
      if (dataPrevisaoFim) dataPrevisaoFim.setHours(0, 0, 0, 0);
      
      let calculatedStatus: 'FINALIZADO' | 'FINALIZAR_CICLO' | 'EM_ANDAMENTO' | 'PROXIMO' = 'PROXIMO';
      
      if (ciclo.status === 'FECHADO_MANUAL' || ciclo.status === 'FECHADO') {
        calculatedStatus = 'FINALIZADO';
      } else if (dataPrevisaoFim && hoje > dataPrevisaoFim) {
        calculatedStatus = 'FINALIZAR_CICLO';
      } else if (dataPrevisaoInicio && dataPrevisaoFim && hoje >= dataPrevisaoInicio && hoje <= dataPrevisaoFim) {
        calculatedStatus = 'EM_ANDAMENTO';
      } else {
        calculatedStatus = 'PROXIMO';
      }
      
      if (periodo === "EM_ANDAMENTO") {
        return calculatedStatus === 'EM_ANDAMENTO';
      }
      if (periodo === "FINALIZAR_CICLO") {
        return calculatedStatus === 'FINALIZAR_CICLO';
      }
      if (periodo === "PROXIMO") {
        return calculatedStatus === 'PROXIMO';
      }
      if (periodo === "FINALIZADOS") {
        return calculatedStatus === 'FINALIZADO';
      }
      if (periodo && typeof periodo === 'object' && 'inicio' in periodo) {
        // Filter by actual dataPrevistaColheita of mangaras (not the tolerance window)
        // Check if any mangará has dataPrevistaColheita within the selected period
        const cicloMangaras = mangaras?.filter(m => m.cicloId === ciclo.id) || [];
        if (cicloMangaras.length === 0) return false;
        
        return cicloMangaras.some(m => {
          if (!m.dataPrevistaColheita) return false;
          const dataPrevista = new Date(m.dataPrevistaColheita);
          dataPrevista.setHours(0, 0, 0, 0);
          return dataPrevista >= periodo.inicio && dataPrevista <= periodo.fim;
        });
      }
      return true;
    });

    // Second pass: calculate metrics for filtered ciclos
    const processedResult = result.map(ciclo => {
      const filteredMangaras = getFilteredMangaras(ciclo.id);
      const isOrfao = filteredMangaras.length === 0 && ciclo.status === 'ATIVO';
      const filteredColheitas = getFilteredColheitas(ciclo.id);
      const filteredPerdas = getFilteredPerdas(ciclo.id);

      const cor = cores?.find(c => c.id === ciclo.corFitaId);
      const variedade = variedades?.find(v => v.id === ciclo.variedadeId);

      // Use filtered data when setor/variedade filters are applied
      // Otherwise use authoritative totals from ciclo (single source of truth)
      const hasFilters = setorId !== "todos" || variedadeId !== "todas";
      
      let planejado, colhido, perda, saldo;
      if (hasFilters) {
        // Calculate from filtered data
        planejado = filteredMangaras.reduce((sum, m) => sum + (m.quantidadeCachos || 0), 0);
        colhido = filteredColheitas.reduce((sum, c) => sum + (c.quantidadeCachos || 0), 0);
        perda = filteredPerdas.reduce((sum, p) => sum + (p.quantidadeCachos || 0), 0);
        saldo = Math.max(0, planejado - colhido - perda);
      } else {
        // Use authoritative totals from ciclo
        planejado = ciclo.totalPlanejado || 0;
        colhido = ciclo.totalColhido || 0;
        perda = ciclo.totalPerdido || 0;
        saldo = Math.max(0, planejado - colhido - perda);
      }
      
      const percentualColhido = planejado > 0 ? (colhido / planejado) * 100 : 0;
      const percentualPerda = planejado > 0 ? (perda / planejado) * 100 : 0;

      // Get first mangara data for mangará week and maturation
      const firstMangara = filteredMangaras[0];
      const semanaMangara = firstMangara?.semanaCorteMangara || 0;
      const semanaPrevista = firstMangara?.semanaPrevistaColheita || 0;
      const anoPrevisto = firstMangara?.anoSemanaPrevistaColheita || new Date().getFullYear();

      // Get maturation weeks from variety
      const mesAnoStr = ciclo.mesAno || '';
      const meses: Record<string, number> = {
        jan: 1, fev: 2, mar: 3, abr: 4, mai: 5, jun: 6,
        jul: 7, ago: 8, set: 9, out: 10, nov: 11, dez: 12
      };
      const mesNum = meses[mesAnoStr.substring(0, 3).toLowerCase()] || 0;
      const semanasMaturacao = variedade?.semanasMaturacaoPorMes?.[mesNum.toString()] || 0;

      // Determine status - same logic as Performance por Setor
      let status: 'FINALIZADO' | 'FINALIZAR_CICLO' | 'EM_ANDAMENTO' | 'PROXIMO' = 'PROXIMO';
      
      if (ciclo.status === 'FECHADO_MANUAL' || ciclo.status === 'FECHADO') {
        status = 'FINALIZADO';
      } else {
        const dataPrevisaoInicio = ciclo.dataPrevisaoInicio ? new Date(ciclo.dataPrevisaoInicio) : null;
        const dataPrevisaoFim = ciclo.dataPrevisaoFim ? new Date(ciclo.dataPrevisaoFim) : null;
        
        if (dataPrevisaoInicio) dataPrevisaoInicio.setHours(0, 0, 0, 0);
        if (dataPrevisaoFim) dataPrevisaoFim.setHours(0, 0, 0, 0);

        if (dataPrevisaoFim && hoje > dataPrevisaoFim) {
          status = 'FINALIZAR_CICLO';
        } else if (dataPrevisaoInicio && dataPrevisaoFim && hoje >= dataPrevisaoInicio && hoje <= dataPrevisaoFim) {
          status = 'EM_ANDAMENTO';
        } else {
          status = 'PROXIMO';
        }
      }

      // Build ciclo code (SXX/YYYY)
      const cicloCode = `S${String(semanaPrevista).padStart(2, '0')}/${anoPrevisto}`;

      return {
        ...ciclo,
        cor,
        variedade,
        planejado,
        colhido,
        perda,
        saldo,
        percentualColhido,
        percentualPerda,
        semanaMangara,
        semanasMaturacao,
        cicloCode,
        status,
        semanaPrevista,
        anoPrevisto,
        isOrfao,
      };
    });

    // Sort by harvest start date (oldest first)
    return processedResult.sort((a, b) => {
      const aDate = a.dataPrevisaoInicio ? new Date(a.dataPrevisaoInicio).getTime() : Infinity;
      const bDate = b.dataPrevisaoInicio ? new Date(b.dataPrevisaoInicio).getTime() : Infinity;
      
      if (aDate === Infinity && bDate === Infinity) {
        if (a.anoPrevisto !== b.anoPrevisto) return a.anoPrevisto - b.anoPrevisto;
        return a.semanaPrevista - b.semanaPrevista;
      }
      
      return aDate - bDate;
    });
  }, [todosOsCiclos, mangaras, colheitas, perdas, cores, variedades, setores, filtroPeriodo, setorId, variedadeId, corFitaId, dataInicio, dataFim]);

  // Busca NO CLIENTE, sobre a lista já montada. Os totais somam o que está na tela
  // — com o campo vazio `combina` passa tudo, então nenhum número muda de valor.
  const dadosVisiveis = useMemo(
    () => filteredData.filter(row => combina(busca, row.cor?.nome, row.variedade?.nome, row.cicloCode)),
    [filteredData, busca],
  );

  // Calculate totals
  const totals = useMemo(() => {
    const totalPlanejado = dadosVisiveis.reduce((sum, row) => sum + row.planejado, 0);
    const totalColhido = dadosVisiveis.reduce((sum, row) => sum + row.colhido, 0);
    const totalPerda = dadosVisiveis.reduce((sum, row) => sum + row.perda, 0);
    const totalSaldo = dadosVisiveis.reduce((sum, row) => sum + row.saldo, 0);
    const percentualColhido = totalPlanejado > 0 ? (totalColhido / totalPlanejado) * 100 : 0;
    const percentualPerda = totalPlanejado > 0 ? (totalPerda / totalPlanejado) * 100 : 0;

    return {
      planejado: totalPlanejado,
      colhido: totalColhido,
      perda: totalPerda,
      saldo: totalSaldo,
      percentualColhido,
      percentualPerda,
    };
  }, [dadosVisiveis]);

  const handleExport = (format: "excel" | "csv") => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataToExport: any[] = filteredData.map(row => ({
        "Ciclo": row.cicloCode,
        "Setor": row.setor?.nome || "-",
        "Variedade": row.variedade?.nome || "-",
        "Cor da Fita": row.cor?.nome || "-",
        "Planejado": row.planejado,
        "Colhido": row.colhido,
        "Perda": row.perda,
        "Saldo": row.saldo,
        "% Colhido": row.percentualColhido?.toFixed(1) || "0",
        "% Perda": row.percentualPerda?.toFixed(1) || "0",
        "Status": row.status || "-",
      }));

      // Add totals row
      dataToExport.push({
        "Ciclo": "TOTAL",
        "Setor": "",
        "Variedade": "",
        "Cor da Fita": "",
        "Planejado": totals.planejado,
        "Colhido": totals.colhido,
        "Perda": totals.perda,
        "Saldo": totals.saldo,
        "% Colhido": totals.percentualColhido.toFixed(1),
        "% Perda": totals.percentualPerda.toFixed(1),
        "Status": "TOTAL",
      });

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Fitas");

      // Auto-adjust column widths
      const colWidths = [12, 12, 15, 15, 12, 12, 10, 10, 12, 12, 18];
      ws["!cols"] = colWidths.map(w => ({ wch: w }));

      const filename = `controle_fitas_${new Date().toISOString().split("T")[0]}.${format === "excel" ? "xlsx" : "csv"}`;
      XLSX.writeFile(wb, filename);

      toast({
        title: "Exportado com sucesso",
        description: `Arquivo ${filename} foi baixado`,
      });
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast({
        title: "Erro ao exportar",
        description: "Não foi possível gerar o arquivo",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'FINALIZADO':
        return <StatusBadge tom="neutro">Finalizado</StatusBadge>;
      case 'FINALIZAR_CICLO':
        return <StatusBadge tom="erro" ponto>Finalizar ciclo</StatusBadge>;
      case 'EM_ANDAMENTO':
        return <StatusBadge tom="sucesso" ponto>Em andamento</StatusBadge>;
      case 'PROXIMO':
        return <StatusBadge tom="info" ponto>Próximo</StatusBadge>;
      default:
        return null;
    }
  };

  /** Fita sem mangará: é um eixo próprio de estado, nunca fundido ao status. */
  const badgeOrfao = <StatusBadge tom="atencao"><AlertTriangle className="h-3 w-3 mr-1" />Sem mangarás</StatusBadge>;

  /** A URL desta lista JÁ tem os filtros — `abrir()` só a grava como origem. */
  const handleRowClick = (cicloId: string) => abrir(`/cacho/ciclos/${cicloId}`);

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader
        titulo="Controle de fitas"
        descricao="Análises e acompanhamento por fita de produção"
        acoesSecundarias={
          <Button
            variant="outline"
            className={ALVO_TOQUE_BOTAO}
            onClick={() => handleExport("csv")}
            data-testid="button-export-csv"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        }
        acao={
          <Button variant="outline" className={ALVO_TOQUE_BOTAO} onClick={() => handleExport("excel")} data-testid="button-export-excel">
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
        }
      />

      <PageToolbar
        fim={
          <PageSearch
            valor={busca}
            onChange={v => definir({ q: v })}
            placeholder="Cor ou setor"
            testId="input-busca-ciclos"
          />
        }
      >
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Setor</Label>
        <Select value={setorId} onValueChange={v => definir({ setor: v })}>
          <SelectTrigger className="w-[180px] shrink-0" data-testid="select-setor">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os Setores</SelectItem>
            {setoresProdutivos?.filter(s => s.ativo).sort((a, b) => a.nome.localeCompare(b.nome)).map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Label className="text-xs text-muted-foreground whitespace-nowrap">Período</Label>
        <Select value={filtroPeriodo} onValueChange={v => definir({ periodo: v })}>
          <SelectTrigger className="w-[180px] shrink-0" data-testid="select-periodo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tudo">Tudo</SelectItem>
            <SelectItem value="em_andamento">Em Andamento</SelectItem>
            <SelectItem value="proximo">Próximo</SelectItem>
            <SelectItem value="finalizar_ciclo">Finalizar Ciclo</SelectItem>
            <SelectItem value="finalizados">Finalizados</SelectItem>
            <SelectItem value="personalizado">Período Específico</SelectItem>
          </SelectContent>
        </Select>

        <Label className="text-xs text-muted-foreground whitespace-nowrap">Variedade</Label>
        <Select value={variedadeId} onValueChange={v => definir({ variedade: v })}>
          <SelectTrigger className="w-[160px] shrink-0" data-testid="select-variedade">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas</SelectItem>
            {variedades?.filter(v => v.ativo).map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Label className="text-xs text-muted-foreground whitespace-nowrap">Cor da fita</Label>
        <Select value={corFitaId} onValueChange={v => definir({ cor: v })}>
          <SelectTrigger className="w-[170px] shrink-0" data-testid="select-cor-fita">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas as Cores</SelectItem>
            {cores?.map((cor) => (
              <SelectItem key={cor.id} value={cor.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: cor.hexColor }}
                  />
                  {cor.nome}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filtroPeriodo === "personalizado" && (
          <>
            <Label className="text-xs text-muted-foreground whitespace-nowrap">De</Label>
            <Input
              type="date"
              value={dataInicio}
              onChange={(e) => definir({ de: e.target.value })}
              className="w-[150px] shrink-0 text-xs"
              data-testid="input-data-inicio"
            />
            <Label className="text-xs text-muted-foreground whitespace-nowrap">até</Label>
            <Input
              type="date"
              value={dataFim}
              onChange={(e) => definir({ ate: e.target.value })}
              className="w-[150px] shrink-0 text-xs"
              data-testid="input-data-fim"
            />
          </>
        )}
      </PageToolbar>

      <DataCard
        titulo={`Fitas — ${filtroPeriodo === "tudo" ? "tudo" :
                 filtroPeriodo === "em_andamento" ? "em andamento" :
                 filtroPeriodo === "proximo" ? "próximo" :
                 filtroPeriodo === "finalizar_ciclo" ? "finalizar ciclo" :
                 filtroPeriodo === "finalizados" ? "finalizados" :
                 "período específico"}`}
        meta={`${dadosVisiveis.length} fita(s)`}
      >
          {isLoading ? (
            <PageSkeleton variante="tabela" linhas={5} className="border-0 rounded-none" />
          ) : filteredData.length === 0 ? (
            <EmptyState
              icone={CalendarIcon}
              titulo="Nenhuma fita no período selecionado"
              descricao="Amplie o período no filtro acima, ou registre o corte do mangará que abre o ciclo."
              testId="empty-ciclos"
            />
          ) : dadosVisiveis.length === 0 ? (
            <NadaEncontrado termo={busca} />
          ) : (
            <>
              {/* Mobile: Cards View */}
              <div className="space-y-2 p-4 md:hidden">
                {dadosVisiveis.map((row, idx) => (
                  <Card
                    key={row.id}
                    className={`border ${row.isOrfao ? 'border-atencao/40' : 'hover-elevate cursor-pointer'}`}
                    onClick={() => !row.isOrfao && handleRowClick(row.id)}
                    data-testid={`fita-card-${idx}`}
                  >
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full border flex-shrink-0"
                            style={{ backgroundColor: row.cor?.hexColor || '#ccc' }}
                          />
                          <span className="font-medium text-sm">{row.cor?.nome || 'Sem cor'}</span>
                          <span className="text-xs text-muted-foreground">• {row.variedade?.nome || 'Sem var.'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {row.isOrfao ? badgeOrfao : getStatusBadge(row.status)}
                          {row.isOrfao && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              onClick={(e) => handleDeleteClick(row as any, e)}
                              data-testid={`delete-orfao-${idx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground font-mono flex gap-3">
                        <span>Ciclo: {row.cicloCode}</span>
                        <span>Mangará: S{String(row.semanaMangara).padStart(2, '0')}</span>
                        <span>+{row.semanasMaturacao}sem</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Planejado:</span>
                          <div className="font-mono font-medium">{row.planejado.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Colhido:</span>
                          <div className="font-mono font-medium">{row.colhido.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">% Colhido:</span>
                          <div className="font-mono font-medium">{row.percentualColhido.toFixed(1)}%</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Perda:</span>
                          <div className="font-mono font-medium text-erro">{row.perda.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Saldo:</span>
                          <div className="font-mono font-medium">{row.saldo.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">% Perda:</span>
                          <div className="font-mono font-medium text-erro">{row.percentualPerda.toFixed(1)}%</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {/* Mobile Totals Card */}
                {dadosVisiveis.length > 0 && (
                  <Card className="border-t-2 border-primary bg-muted/50" data-testid="totals-card-mobile">
                    <CardContent className="p-3 space-y-2">
                      <div className="text-xs font-bold text-muted-foreground">
                        TOTAIS (VISUALIZAÇÃO ATUAL)
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Planejado:</span>
                          <div className="font-mono font-bold">{totals.planejado.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Colhido:</span>
                          <div className="font-mono font-bold">{totals.colhido.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">% Colhido:</span>
                          <div className="font-mono font-bold">{totals.percentualColhido.toFixed(1)}%</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Perda:</span>
                          <div className="font-mono font-bold text-erro">{totals.perda.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Saldo:</span>
                          <div className="font-mono font-bold">{totals.saldo.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">% Perda:</span>
                          <div className="font-mono font-bold text-erro">{totals.percentualPerda.toFixed(1)}%</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Desktop: Table View */}
              <div className="hidden md:block overflow-x-auto">
                {/* Larguras MEDIDAS, não estimadas. As antigas (11/9/8/7×8/11rem)
                    somavam 95rem = 1.520px e obrigavam scroll horizontal em tudo
                    abaixo de 1920px de tela — inclusive nos 1600px do Hugo, onde a
                    coluna "% perda" ficava cortada.

                    Medido no navegador com o CSS compilado, o mínimo real de cada
                    coluna é max(texto do cabeçalho, maior dado) + 24px de `px-3`.
                    Em quase toda coluna numérica quem manda é o CABEÇALHO, não o
                    número: "Maturação" precisa de 101px e o "+20" só de 46. Soma
                    dos mínimos: 1.097px — havia 423px de folga inventada.

                    A primeira coluna fica ELÁSTICA (`null`) para absorver a sobra,
                    e o `min-w` passa a ser o mínimo medido em vez de um número
                    redondo: abaixo dele a rolagem volta, que é o certo — 12 colunas
                    não cabem em tela estreita, e `overflow-x-auto` existe para isso. */}
                <DataTable
                  colunas={[null, "6rem", "5.5rem", "5.5rem", "6.5rem", "6.25rem", "5.25rem", "6.25rem", "4.5rem", "5.25rem", "4.5rem", "8.5rem"]}
                  className="min-w-[72.5rem]"
                >
                  <DataTableHeader>
                    <DataTableRow>
                      <DataTableHead>Cor / fita</DataTableHead>
                      <DataTableHead>Variedade</DataTableHead>
                      <DataTableHead>Ciclo</DataTableHead>
                      <DataTableHead>Mangará</DataTableHead>
                      <DataTableHead num>Maturação</DataTableHead>
                      <DataTableHead num>Planejado</DataTableHead>
                      <DataTableHead num>Colhido</DataTableHead>
                      <DataTableHead num>% colhido</DataTableHead>
                      <DataTableHead num>Perda</DataTableHead>
                      <DataTableHead num>% perda</DataTableHead>
                      <DataTableHead num>Saldo</DataTableHead>
                      <DataTableHead>Status</DataTableHead>
                    </DataTableRow>
                  </DataTableHeader>
                  <DataTableBody>
                    {dadosVisiveis.map((row, idx) => (
                      <DataTableRow
                        key={row.id}
                        className={row.isOrfao ? 'bg-atencao-suave hover:bg-atencao-suave' : 'cursor-pointer'}
                        onClick={() => !row.isOrfao && handleRowClick(row.id)}
                        data-testid={`fita-row-${idx}`}
                      >
                        {/* A cor da fita é DADO (`cores.hexColor`): entra por `style`,
                            nunca por classe do Tailwind. */}
                        <DataTableCell destaque>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full border flex-shrink-0"
                              style={{ backgroundColor: row.cor?.hexColor || '#ccc' }}
                            />
                            <span className="truncate">{row.cor?.nome || 'Sem cor'}</span>
                          </div>
                        </DataTableCell>
                        <DataTableCell className="truncate">{row.variedade?.nome || '-'}</DataTableCell>
                        <DataTableNum className="text-left">{row.cicloCode}</DataTableNum>
                        <DataTableNum className="text-left">{row.isOrfao ? '—' : `S${String(row.semanaMangara).padStart(2, '0')}`}</DataTableNum>
                        <DataTableNum>{row.isOrfao ? '—' : `+${row.semanasMaturacao}`}</DataTableNum>
                        <DataTableNum>{row.isOrfao ? '—' : row.planejado.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum>{row.isOrfao ? '—' : row.colhido.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum>{row.isOrfao ? '—' : `${row.percentualColhido.toFixed(1)}%`}</DataTableNum>
                        <DataTableNum className="text-erro">{row.isOrfao ? '—' : row.perda.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="text-erro">{row.isOrfao ? '—' : `${row.percentualPerda.toFixed(1)}%`}</DataTableNum>
                        <DataTableNum>{row.isOrfao ? '—' : row.saldo.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableCell>
                          {row.isOrfao ? (
                            <div className="flex items-center gap-2">
                              {badgeOrfao}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                onClick={(e) => handleDeleteClick(row as any, e)}
                                data-testid={`delete-orfao-desktop-${idx}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : getStatusBadge(row.status)}
                        </DataTableCell>
                      </DataTableRow>
                    ))}
                    {/* Totals Row */}
                    {dadosVisiveis.length > 0 && (
                      <DataTableRow className="bg-muted/50 hover:bg-muted/50 border-t-2" data-testid="totals-row">
                        <DataTableCell colSpan={5} className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Totais (visualização atual)
                        </DataTableCell>
                        <DataTableNum className="font-semibold">{totals.planejado.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="font-semibold">{totals.colhido.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="font-semibold">{totals.percentualColhido.toFixed(1)}%</DataTableNum>
                        <DataTableNum className="font-semibold text-erro">{totals.perda.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="font-semibold text-erro">{totals.percentualPerda.toFixed(1)}%</DataTableNum>
                        <DataTableNum className="font-semibold">{totals.saldo.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableCell />
                      </DataTableRow>
                    )}
                  </DataTableBody>
                </DataTable>
              </div>
            </>
          )}
      </DataCard>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              {cicloToDelete && (
                <>
                  Tem certeza que deseja excluir o ciclo <strong>{cicloToDelete.codigo}</strong>?
                  <br />
                  <br />
                  Esta ação só é permitida se não houver mangarás, colheitas ou perdas associadas a este ciclo.
                  Esta operação não pode ser desfeita.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-ciclo-button-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-ciclo-button-confirm"
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
